import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../../components/common/Header/Header";
import Modal from "../../../components/common/modal/modal";
import ContactDetails from "../../../components/newPersonalAccidentPolicy/personalAccidentFlowSteps/contactDetails/contactDetails";
import PaymentSummary from "../../../components/newPersonalAccidentPolicy/personalAccidentFlowSteps/pay/paymentSummary";
import PersonalInfo from "../../../components/newPersonalAccidentPolicy/personalAccidentFlowSteps/personalInfo/personalInfo";
import StartPage from "../../../components/newPersonalAccidentPolicy/personalAccidentFlowSteps/start/startPage";
import {
  STEP_NAME_PAY,
  STEP_NAME_PERSONAL_INFO,
  STEP_NAME_START,
} from "../../../hooks/data";
import usePersonalAccidentFlow from "../../../hooks/usePersonalAccidentFlow";
import { clearPersonalAccidentFlow } from "../../../redux/features/personalAccidentFlowSlice";
import { clearNewPersonalAccidentFormData } from "../../../redux/features/personalAccidentFormSlice";
import { personalAccidentStepperData } from "./data";

const NewPersonalAccidentPolicy = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(true);
  const { currentStep, currentSubstep } = usePersonalAccidentFlow();

  // Clear payment redirect flag on component mount (user returned from payment or started fresh)
  useEffect(() => {
    const wasRedirectingToPayment = sessionStorage.getItem("isRedirectingToPayment");
    if (wasRedirectingToPayment === "true") {
      console.log("🔓 Clearing payment redirect flag - user returned or component mounted");
      sessionStorage.removeItem("isRedirectingToPayment");
    }
  }, []);

  // Clear form data when component unmounts or when user navigates away
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Don't clear if we're redirecting to payment
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset - redirecting to payment");
        return;
      }
      dispatch(clearPersonalAccidentFlow());
      dispatch(clearNewPersonalAccidentFormData());
    };

    const handlePopState = () => {
      // Don't clear if we're redirecting to payment
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset - redirecting to payment");
        return;
      }
      dispatch(clearPersonalAccidentFlow());
      dispatch(clearNewPersonalAccidentFormData());
    };

    // Add event listeners for browser navigation
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    // Cleanup function - runs when component unmounts
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);

      // CRITICAL: Don't clear flow state if we're redirecting to payment
      // This prevents the brief flash of the start screen before redirect
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset on unmount - redirecting to payment");
        return;
      }

      // Clear form data when component unmounts
      dispatch(clearPersonalAccidentFlow());
      dispatch(clearNewPersonalAccidentFormData());
    };
  }, [dispatch]);

  // Clear form data when user navigates away from the personal accident policy route
  useEffect(() => {
    const currentPath = location.pathname;

    return () => {
      // Don't clear if we're redirecting to payment
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset on route change - redirecting to payment");
        return;
      }

      // Only clear if we're leaving the personal accident policy routes
      if (!currentPath.includes("/personal-accident")) {
        dispatch(clearPersonalAccidentFlow());
        dispatch(clearNewPersonalAccidentFormData());
      }
    };
  }, [location.pathname, dispatch]);

  const activeStepData = useMemo(() => {
    let activeStep = personalAccidentStepperData.find(
      (each) => each.key === currentStep.name
    );
    return activeStep;
  }, [currentStep]);

  const PageRenderer = () => {
    // Always show StartPage if currentStep is undefined or at the start
    if (!currentStep?.name || currentStep?.name === STEP_NAME_START) {
      return <StartPage onClose={handleModalClose} />;
    }

    switch (currentStep?.name) {
      case STEP_NAME_PERSONAL_INFO:
        // Handle substeps for personal info
        if (currentSubstep === "contact_details") {
          return <ContactDetails />;
        }
        return <PersonalInfo />; // Default to required_information substep
      case STEP_NAME_PAY:
        // Default to review_and_confirm substep (payment summary)
        return <PaymentSummary />;
      default:
        return <StartPage onClose={handleModalClose} />; // Fallback to start page
    }
  };

  const handleModalClose = () => {
    // Reset personal accident flow and form data when modal is closed
    dispatch(clearPersonalAccidentFlow());
    dispatch(clearNewPersonalAccidentFormData());
    setShowModal(false);
    navigate("/");
  };

  return (
    <Modal show={showModal}>
      <Header
        isPopup
        hideMobileClose
        onCloseModal={handleModalClose}
        showStepper={true}
        steps={personalAccidentStepperData}
        activeStep={activeStepData}
      />
      <div id="pa-back-portal" />
      <div className="mainPersonalAccidentModal pt-5 relative pb-[5%] max767:w-full max767:overflow-y-scroll max767:pb-[30%] max767:h-screen ipad:pt-[10%] ipad:h-[93vh] ipad:overflow-y-scroll max767:pt-0">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={currentStep?.name || "start"}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
            {PageRenderer()}
          </motion.div>
        </AnimatePresence>
        {/* Stable mount point for the bottom sticky footer button, kept
            outside the animated/transformed wrapper to prevent flicker. */}
        <div id="pa-footer-portal" />
      </div>
    </Modal>
  );
};

export default NewPersonalAccidentPolicy;
